import axios from 'axios'
import { TokenStorage, handleApiError, ApiResponse } from './apiConfig'

// Interfaces pour les données
export interface RegisterData {
  nom: string
  prenom: string
  telephone: string
  email: string
  salaire: number
  quotiteCessiblePourcent: number
  quotiteCessibleMontant: number
  paysId: number
  collectiviteId: number
  dateNaissance: string
  dateEngagement: string
  tokenFcm?: string
  codeSecurite?: string
}

export interface LoginData {
  telephone?: string
  email?: string
  codeSecurite: string
}

export interface LoginRequestData {
  identifiant: string
  codeSecurite: string
}

export interface VerifyOtpData {
  identifiant: string
  otp: string
}

export interface UserData {
  token: string
  id: number
  nom: string
  prenom: string
  photo?: string
  photo_url?: string
  avatar?: string
  avatar_url?: string
  actif: boolean
  codeSecurite: string
  telephone: string
  email: string
  fonction?: string
  is_gerant?: boolean
  salaire?: number
  age?: number
  anciennete?: number
  quotiteCessiblePourcent?: number
  quotiteCessibleMontant?: number
  dateNaissance?: string
  dateEngagement?: string
  paysCode?: string
  paysId?: number
  paysNom?: string
  collectivite_id?: number
  collectivite?: {
    id: number
    nom: string
  }
  tokenFcm?: string
  piece_identite_type?: string
  numero_piece?: string
  piece_recto?: string
  piece_verso?: string
  bulletin1?: string
  bulletin2?: string
  bulletin3?: string
}

class AuthService {
  /**
   * Vérifier si un numéro de téléphone existe
   */
  async checkPhone(telephone: string): Promise<ApiResponse<UserData>> {
    try {
      const { data } = await axios.post('/login/check', {
        telephone: telephone.replace(/\s/g, '')
      })

      if (data.status === 0 && data.data) {
        TokenStorage.setToken(data.data.token)
        TokenStorage.setUser(data.data)
      }

      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Vérifier un numéro de téléphone via OTP (envoi SMS/WhatsApp)
   */
  async verifyPhone(telephone: string): Promise<ApiResponse> {
    try {
      const { data } = await axios.post('/login/verify/phone', { telephone })
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Vérifier l'OTP téléphone
   */
  async checkPhoneOtp(otp: string, telephone: string): Promise<ApiResponse> {
    try {
      const { data } = await axios.post('/login/check/phone', { otp, telephone })

      if (data.status === 0 && data.data) {
        TokenStorage.setToken(data.data.token)
        TokenStorage.setUser(data.data)
      }

      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Inscription complète avec souscription (endpoint unifié avec mobile)
   */
  async activate(userData: RegisterData): Promise<ApiResponse<UserData>> {
    try {
      const { data } = await axios.post('/activate', userData)

      if (data.status === 0 && data.data) {
        TokenStorage.setToken(data.data.token)
        TokenStorage.setUser(data.data)
      }

      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Inscription d'un nouvel utilisateur (legacy - utiliser activate() de préférence)
   */
  async register(userData: RegisterData): Promise<ApiResponse<UserData>> {
    try {
      const { data } = await axios.post('/register', userData)

      if (data.status === 0 && data.data) {
        TokenStorage.setToken(data.data.token)
        TokenStorage.setUser(data.data)
      }

      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Étape 1 : vérifier les identifiants et envoyer l'OTP
   */
  async requestLogin(payload: LoginRequestData): Promise<ApiResponse> {
    try {
      const identifier = payload.identifiant.trim()
      const body: Record<string, string> = {
        codeSecurite: payload.codeSecurite.trim()
      }

      if (identifier.includes('@')) {
        body.email = identifier
      } else {
        body.telephone = identifier
      }

      const { data } = await axios.post('/login/otp/request', body)
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Étape 2 : vérifier l'OTP et finaliser la connexion
   */
  async verifyOtp(payload: VerifyOtpData): Promise<ApiResponse> {
    try {
      const identifier = payload.identifiant.trim()
      const body: Record<string, string> = {
        otp: payload.otp.trim()
      }

      if (identifier.includes('@')) {
        body.email = identifier
      } else {
        body.telephone = identifier
      }

      const { data } = await axios.post('/login/otp/verify', body)

      const responseData = data.data || data
      const token = responseData.token || data.token

      if (data.status === 0 && token) {
        TokenStorage.setToken(token)

        const userData = {
          id: responseData.user_id ?? responseData.id,
          pseudo: responseData.pseudo,
          prenom: responseData.prenom,
          nom: responseData.nom,
          email: responseData.email,
          phone: responseData.phone || responseData.telephone,
          telephone: responseData.phone || responseData.telephone,
          is_gerant: responseData.is_gerant,
          collectivite_id: responseData.collectivite_id,
          collectivite: responseData.collectivite,
          pays: responseData.pays,
          token: token
        }
        TokenStorage.setUser(userData)
      }

      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  async login(credentials: LoginData): Promise<ApiResponse> {
    const identifier = credentials.email ?? credentials.telephone ?? ''
    return this.verifyOtp({
      identifiant: identifier,
      otp: credentials.codeSecurite
    })
  }

  /**
   * Définir le code PIN de sécurité (après inscription)
   */
  async setPin(codepin: string): Promise<ApiResponse> {
    try {
      const { data } = await axios.post('/login/pin', { codepin })
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Envoyer un code OTP par email
   */
  async verifyEmail(): Promise<ApiResponse> {
    try {
      const { data } = await axios.post('/login/verify/email', {})
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Envoyer un OTP pour la connexion (sans authentification)
   */
  async sendLoginOtp(username: string): Promise<ApiResponse> {
    try {
      const { data } = await axios.post('/login/send-otp', { username })
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Vérifier le code OTP reçu par email
   */
  async checkEmail(otp: string): Promise<ApiResponse> {
    try {
      const { data } = await axios.post('/login/check/email', { otp })
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Demander la réinitialisation du mot de passe
   */
  async forgotPassword(username: string): Promise<ApiResponse> {
    try {
      const { data } = await axios.post('/login/forgot', { username })
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Vérifier l'OTP pour la réinitialisation du mot de passe
   */
  async verifyOtpForReset(username: string, otp: string): Promise<ApiResponse<{ resetToken: string }>> {
    try {
      const { data } = await axios.post('/reset-password/verify-otp', { username, otp })

      if (data.status === 0 && data.resetToken) {
        sessionStorage.setItem('reset_token', data.resetToken)
      }

      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Définir un nouveau code de sécurité avec le token de reset
   */
  async resetPasswordWithToken(codepin: string): Promise<ApiResponse> {
    try {
      const resetToken = sessionStorage.getItem('reset_token')

      if (!resetToken) {
        return {
          status: 3,
          message: 'Token de réinitialisation introuvable'
        }
      }

      const { data } = await axios.post('/reset-password/set-pin', { codepin }, {
        headers: { 'Authorization': `Bearer ${resetToken}` }
      })

      if (data.status === 0) {
        sessionStorage.removeItem('reset_token')
      }

      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Récupérer le statut KYC
   */
  async getKycStatus(): Promise<ApiResponse<{ kyc: boolean }>> {
    try {
      const { data } = await axios.get('/kyc/statut')
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Sauvegarder les documents KYC
   */
  async saveKyc(formData: FormData): Promise<ApiResponse> {
    try {
      const { data } = await axios.post('/kyc/save', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Déconnexion
   */
  async logout(): Promise<ApiResponse> {
    try {
      const { data } = await axios.post('/logout', {})
      TokenStorage.clear()
      return data
    } catch (error) {
      TokenStorage.clear()
      return handleApiError(error)
    }
  }

  /**
   * Vérifier si l'utilisateur est authentifié
   */
  isAuthenticated(): boolean {
    return TokenStorage.getToken() !== null
  }

  /**
   * Récupérer les données de l'utilisateur connecté
   */
  getCurrentUser(): UserData | null {
    return TokenStorage.getUser()
  }

  /**
   * Récupérer le profil complet de l'utilisateur
   */
  async getProfile(): Promise<ApiResponse<UserData>> {
    try {
      const { data } = await axios.get('/profile')
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Mettre à jour le profil de l'utilisateur
   */
  async updateProfile(profileData: Partial<UserData>): Promise<ApiResponse> {
    try {
      const { data } = await axios.put('/profile/update', profileData)

      if (data.status === 0) {
        const currentUser = this.getCurrentUser()
        if (currentUser) {
          TokenStorage.setUser({ ...currentUser, ...profileData })
        }
      }

      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Mettre à jour la photo de profil
   */
  async updatePhoto(photoFile: File): Promise<ApiResponse<{ photo: string }>> {
    try {
      const formData = new FormData()
      formData.append('photo', photoFile)

      const { data } = await axios.post('/profile/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Récupérer les préférences de l'utilisateur
   */
  async getPreferences(): Promise<ApiResponse<{
    theme: string
    notifications: {
      push: boolean
      email: boolean
      sms: boolean
    }
  }>> {
    try {
      const { data } = await axios.get('/profile/preferences')
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Mettre à jour les préférences de l'utilisateur
   */
  async updatePreferences(preferences: {
    theme?: string
    notifications?: {
      push?: boolean
      email?: boolean
      sms?: boolean
    }
  }): Promise<ApiResponse> {
    try {
      const { data } = await axios.post('/profile/preferences', preferences)
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Calculer la souscription
   */
  async calculSouscription(params: {
    salaire: number
    prelevementPourcent: number
    prelevementMontant: number
    quotiteCessiblePourcent: number
    quotiteCessibleMontant: number
  }): Promise<ApiResponse> {
    try {
      const { data } = await axios.get('/souscription/calcul', { params })
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }
}

export default new AuthService()
